'use client';

import React, { useState, useRef, useEffect, ReactNode } from 'react';
import classnames from 'classnames';
import Box from '../../atoms/Box/Box';
import Typography from '../../atoms/Typography/Typography';
import Iconography from '../../atoms/Iconography/Iconography';
import styles from './Accordion.module.scss';

interface AccordionProps {
  title: string;
  children: ReactNode;
  theme?: 'light' | 'dark';
  defaultOpen?: boolean;
  className?: string;
  showBorder?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  theme = 'dark',
  defaultOpen = false,
  className,
  showBorder = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [height, setHeight] = useState<number | undefined>(defaultOpen ? undefined : 0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      const contentEl = contentRef.current;
      if (contentEl) {
        setHeight(contentEl.scrollHeight);
      }
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, [children, isOpen]);

  return (
    <Box
      className={classnames(styles.accordion, { [styles.withBorder]: showBorder }, className)}
      data-theme={theme}
    >
      <button
        className={styles.header}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        type="button"
      >
        <Typography
          variant="label"
          size="medium"
          theme={theme}
          color="white"
          className={styles.title}
        >
          {title}
        </Typography>
        <Iconography
          category="arrows"
          name="chevron-down"
          theme={theme}
          color="white"
          className={classnames(styles.icon, { [styles.rotated]: isOpen })}
        />
      </button>
      {/* eslint-disable-next-line react/forbid-dom-props -- Dynamic maxHeight required for animated collapse transition */}
      <div className={styles.contentWrapper} style={{ maxHeight: height }} ref={contentRef}>
        <Box className={styles.content}>{children}</Box>
      </div>
    </Box>
  );
};

Accordion.displayName = 'Accordion';

export default Accordion;
